import { Component, effect, inject, OnDestroy } from '@angular/core';

import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  IonBackButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonItem,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone';
import { BASE_CONFIG } from '@libs/core/base-config';
import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { VxSubmitButtonComponent } from '@libs/core/lib/form-controls/mtjr-submit-button.component';
import { VxTextAreaFieldComponent } from '@libs/core/lib/form-controls/mtjr-text-field.component';
import { errorState, initialState } from '@libs/core/lib/state/async-states';
import { Failure, LocalFailure } from '@libs/core/lib/state/failure';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { withCancelOrder } from '@libs/mtjr/shared/generated/entities/order/order-store-features';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

const OrderCancelStore = signalStore(
  withCancelOrder(),
  withMethods((store) => ({
    setFailure(failure: Failure | null) {
      patchState(store, { cancelOrderState: failure ? errorState(failure) : initialState() });
    },
  })),
);

@Component({
  selector: 'mtjr-cancel-order',
  standalone: true,
  providers: [OrderCancelStore],
  imports: [
    IonContent,
    IonHeader,
    IonTitle,
    IonToolbar,
    ReactiveFormsModule,
    FormsModule,
    VxTextAreaFieldComponent,
    TranslatePipe,
    IonRadioGroup,
    IonRadio,
    IonLabel,
    IonItem,
    IonButtons,
    IonBackButton,
    VxErrorBarComponent,
    VxSubmitButtonComponent,
  ],
  template: `
    <ion-header [translucent]="true">
      <ion-toolbar>
        <ion-title>{{ 'ORDERS.MARK_AS_CANCELED' | translate }}</ion-title>
        <ion-buttons slot="start">
          <ion-back-button
            [text]="'COMMON.BACK' | translate"
            [defaultHref]="flavor === 'vendors' ? '/main/orders' : '/orders'"
          />
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content>
      <div class="flex flex-col  pb-6 max-w-[550px] mx-auto">
        <form [formGroup]="cancelOrderForm" (ngSubmit)="cancelOrder()">
          <div class="mt-2 px-5 py-3">
            <ion-radio-group [value]="cancelOrderForm.controls.reason.value">
              @for (reason of orderCancelReasons; track $index) {
                <ion-item class="my-2" (click)="cancelOrderForm.controls.reason.setValue(reason)">
                  <ion-radio
                    mode="md"
                    slot="start"
                    [value]="reason"
                    (ionChange)="cancelOrderForm.controls.reason.setValue(reason)"
                  ></ion-radio>
                  <ion-label class="capitalize">
                    {{ reason | translate }}
                  </ion-label>
                </ion-item>
              }
            </ion-radio-group>
            <div class="my-6"></div>
            <mtjr-text-area-field
              [field]="cancelOrderForm.controls.otherReason"
              [label]="'ORDERS.CANCEL_REASON' | translate"
              [placeholder]="'ORDERS.CANCEL_REASON_PLACEHOLDER' | translate"
            />
            <div class="my-6"></div>
            <mtjr-error-bar [failure]="store.cancelOrderFailure()" />
          </div>
        </form>
        <div class="mx-5 flex justify-end mt-">
          <mtjr-submit-button
            [label]="'ORDERS.MARK_AS_CANCELED' | translate"
            [state]="store.cancelOrderState()"
            (click)="cancelOrder()"
            color="danger"
          ></mtjr-submit-button>
        </div>
      </div>
    </ion-content>
  `,
})
export class CancelOrderPage implements OnDestroy {
  fb = inject(FormBuilder);
  store = inject(OrderCancelStore);
  appAlerts = inject(AppAlerts);
  formSubscription?: Subscription;
  route = inject(ActivatedRoute);
  translate = inject(TranslateService);
  eventBus = inject(NgEventBus);
  flavor = inject(BASE_CONFIG).flavor;

  cancelOrderForm = this.fb.group({
    reason: this.fb.control<string | null>(null, [Validators.required]),
    otherReason: this.fb.control<string | null>(null, []),
  });

  orderCancelReasons = [
    'ORDERS.CANCEL_REASON_OUT_OF_STOCK',
    'ORDERS.CANCEL_REASON_CUSTOMER_REQUEST',
    'ORDERS.CANCEL_REASON_WRONG_ORDER',
    'ORDERS.CANCEL_REASON_OTHER',
  ];

  constructor() {
    const otherReasonField = this.cancelOrderForm.controls.otherReason;
    otherReasonField.disable();
    this.formSubscription = this.cancelOrderForm.controls.reason.valueChanges.subscribe((value) => {
      this.store.setFailure(null);
      if (value === 'ORDERS.CANCEL_REASON_OTHER') {
        otherReasonField.setValidators([Validators.required, Validators.minLength(10)]);
        otherReasonField.enable();
      } else {
        otherReasonField.clearValidators();
        otherReasonField.disable();
      }
      otherReasonField.updateValueAndValidity();
    });

    effect(() => {
      if (this.store.cancelOrderState().success) {
        this.appAlerts.successSnackbarTR('ORDERS.ORDER_CANCELED_SUCCESS');
        window.history.back();
        this.eventBus.cast('order:fetch', { orderId: this.route.snapshot.params['orderId'] });
      }
    });
  }

  async cancelOrder() {
    if (this.cancelOrderForm.invalid) {
      this.cancelOrderForm.markAllAsTouched();
      if (this.cancelOrderForm.controls.reason.invalid) {
        this.store.setFailure(new LocalFailure('ORDERS.CANCEL_REASON_REQUIRED'));
      }
      return;
    }

    const orderId = this.route.snapshot.params['orderId'];
    const subOrderId = this.route.snapshot.params['subOrderId'];
    const reason = this.cancelOrderForm.controls.reason.value;

    if (!orderId || !subOrderId || !reason) {
      this.store.setFailure(new LocalFailure('COMMON.SOMETHING_WENT_WRONG'));
      return;
    }

    const reasonText =
      reason === 'ORDERS.CANCEL_REASON_OTHER'
        ? this.cancelOrderForm.controls.otherReason.value
        : this.translate.instant(reason);

    this.store.cancelOrder({ reason: reasonText }, { orderId, subOrderId });
  }

  ngOnDestroy() {
    this.formSubscription?.unsubscribe();
  }
}
